import React, { useState, useEffect } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  format,
  isSameMonth,
  isSameDay,
  parseISO
} from "date-fns";
import { useAuth0 } from "@auth0/auth0-react";
import { getEvents } from "../../services/event-service";
import { isUserSiteAdmin } from "../shared/utilities";
import "../../styles/components/events.css";
import { Link, useNavigate } from "react-router-dom";

export default function EventCalendar({ events = [], isHomePage = false, isTV = false }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const isEventAdmin = isUserSiteAdmin(user);
  const [eventList, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchEvents() {
      const accessToken = await getAccessTokenSilently();
      const payload = {};
      const eventsReturned = await getEvents(accessToken, payload);
      setIsLoading(false);
      setEvents(eventsReturned.data);
    }
    fetchEvents();
  }, []);

  const renderHeader = () => (
    <div className="calendar-header">
      <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>‹</button>
      {isTV ? (
        <div className="calendar-header-tv-center">
          <img
            src="https://azdfmlyappsa.blob.core.windows.net/images/fmlyLogo.png"
            alt="Family Logo"
            className="calendar-tv-logo"
          />
          <span>{format(currentMonth, "MMMM yyyy")}</span>
        </div>
      ) : (
        <span>{format(currentMonth, "MMMM yyyy")}</span>
      )}
      <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>›</button>
    </div>
  );

  const renderDays = () => {
    const days = [];
    const dateFormat = "EEE";

    let startDate = startOfWeek(currentMonth, { weekStartsOn: 0 });

    for (let i = 0; i < 7; i++) {
      days.push(
        <div className="calendar-day-name" key={i}>
          {format(addDays(startDate, i), dateFormat)}
        </div>
      );
    }

    return <div className="calendar-days-row">{days}</div>;
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const formattedDate = format(day, "d");
        const isCurrentDay = isSameDay(day, new Date());
        const isCurrentMonth = isSameMonth(day, monthStart);

        const dayEvents = eventList?.events?.filter((event) =>
          isSameDay(parseISO(event.date), cloneDay)
        );

        const gameEvent = dayEvents?.find(
          (event) =>
            event.eventLocationID === 1 ||
            event.eventLocationID === 3 ||
            event.eventLocationID === "1" ||
            event.eventLocationID === "3"
        );

        const nonExternalEvents = dayEvents?.filter(
          (event) =>
            event.eventLocationID !== 1 &&
            event.eventLocationID !== 3
        );

        const externalEvents = dayEvents?.filter(
          (event) =>
            event.eventLocationID === 1 ||
            event.eventLocationID === 3
        );

        const externalEvent = externalEvents?.[0];
        const locationID = externalEvent?.eventLocationID ? parseInt(externalEvent.eventLocationID) : null;

        let backgroundHex = "";
        if (locationID === 3) {
          backgroundHex = "#34322fe8";
        } else if (locationID !== null) {
          backgroundHex = "#c13d53c4";
        }

        const showImage = externalEvent?.imageURL;

        days.push(
          <div
            className={`calendar-cell ${!isCurrentMonth ? "disabled" : ""}`}
            key={day}
            style={{ background: backgroundHex }}
            onClick={() => {
              if (!isTV || !gameEvent || !isCurrentMonth) return;
              navigate(`/events/${gameEvent.id}/tv`);
            }}
            onKeyDown={(event) => {
              if (!isTV || !gameEvent || !isCurrentMonth) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                navigate(`/events/${gameEvent.id}/tv`);
              }
            }}
            role={isTV && gameEvent && isCurrentMonth ? "button" : undefined}
            tabIndex={isTV && gameEvent && isCurrentMonth ? 0 : -1}
            aria-label={isTV && gameEvent && isCurrentMonth ? `Open game event for ${format(cloneDay, "MMMM d")}` : undefined}
          >
            {showImage && (
              <div
                className="calendar-background-image"
                style={{ backgroundImage: `url(${externalEvent.imageURL})` }}
              />
            )}
            <div className={`calendar-date ${isCurrentDay ? "current-day" : ""}`}>
              {formattedDate}
            </div>

            {/* Show non-external events */}
            {nonExternalEvents?.filter((event, index, self) => 
              index === self.findIndex(e => e.name === event.name)
            ).map((event, idx) => (
              <a className="calendar-event-name" 
                key={idx}
                href={`/events/${event.id}${isTV ? '/tv' : ''}`}
                onClick={(clickEvent) => clickEvent.stopPropagation()}
              >
                {event.name}
              </a>
            ))}

            {/* Show external events */}
            {externalEvents?.filter((event) => 
              event.eventLocationID !== 3
            ).filter((event, index, self) => 
              index === self.findIndex(e => e.name === event.name)
            ).map((event, idx) => (
              <a className="calendar-event-name" 
                key={`external-${idx}`}
                href={`/events/${event.id}${isTV ? '/tv' : ''}`}
                onClick={(clickEvent) => clickEvent.stopPropagation()}
              >
                {event.name}
              </a>
            ))}
          </div>
        );

        day = addDays(day, 1);
      }

      rows.push(
        <div className="calendar-row" key={day}>
          {days}
        </div>
      );

      days = [];
    }

    return <div className="calendar-body">{rows}</div>;
  };

  return (
  <div className="calendar-container">
    {renderHeader()}
    {isLoading && <div style={{fontSize: "1.2rem"}}>Loading events...</div>}
    {/* Legend directly under header */}
    <div className="calendar-legend" style={{ display: "flex", gap: "1rem", alignItems: "center", margin: "0.5rem 0", padding: "8px" }}>
      <div className="calendar-legend-item" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <span className="calendar-legend-swatch" style={{ width: "16px", height: "16px", backgroundColor: "#a71930", display: "inline-block", borderRadius: "3px" }}></span>
        <span>Home Game</span>
      </div>
      <div className="calendar-legend-item" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <span className="calendar-legend-swatch" style={{ width: "16px", height: "16px", backgroundColor: "#34322fe8", display: "inline-block", borderRadius: "3px" }}></span>
        <span>Away Game</span>
      </div>

      {/* Spacer + Create Event Button */}
      {isEventAdmin && !isHomePage && (
        <Link
          to="/events/new"
          className="create-event-btn"
        >
          <i className="fa-solid fa-plus" style={{ marginRight: "6px", fontSize: "12px" }}></i>
          Create Event
        </Link>
      )}
    </div>

    {renderDays()}
    {renderCells()}
  </div>
);
}